import { useState, useEffect, useCallback, type FC } from "react";
import { useNavigate, useParams } from "react-router-dom";
import NavbarSidebarLayout from "../layouts/navbar-sidebar";
import {
  Breadcrumb,
  Button,
  Card,
  Label,
  Spinner,
  Badge,
  Alert,
  Table,
} from "flowbite-react";
import {
  HiHome,
  HiChevronLeft,
  HiTrash,
  HiPaperAirplane,
  HiCheckCircle,
  HiClock,
  HiChartBar,
  HiMail,
  HiPhone,
  HiUserGroup,
  HiCalendar,
  HiInformationCircle,
  HiTrendingUp,
  HiExclamationCircle,
  HiXCircle,
  HiX,
  HiLightningBolt,
  HiRefresh,
} from "react-icons/hi";
import { FiMail, FiMessageSquare, FiPhone, FiAlertTriangle, FiPhoneOff } from "react-icons/fi";
import { campaignService } from "../services/main";
import toast from "react-hot-toast";

interface DeliveryRecord {
  id: number;
  contactId: number;
  contact: {
    id: number;
    firstName: string;
    lastName: string;
    email: string | null;
  };
  channel: string;
  status: string;
  sentAt: string | null;
  errorMessage: string | null;
  callDuration?: number | null;
  callRecordingUrl?: string | null;
  deviceId?: string | null;
  createdAt: string;
}

interface CampaignAnalytics {
  campaign: {
    id: number;
    name: string;
    status: string;
  };
  metrics: {
    total: number;
    sent: number;
    delivered: number;
    bounced: number;
    failed: number;
    answered?: number;
    noAnswer?: number;
    busy?: number;
    rejected?: number;
    disconnected?: number;
  };
  rates: {
    deliveryRate: number;
  };
  channelBreakdown: {
    EMAIL: number;
    SMS: number;
    WHATSAPP: number;
    TELEPHONY: number;
  };
  timeline?: Array<{
    date: string;
    sent: number;
    delivered: number;
  }>;
  funnel?: {
    sent: number;
    delivered: number;
  };
  deliveryRecords?: DeliveryRecord[];
}

const CampaignDetailPage: FC = function () {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [campaign, setCampaign] = useState<any>(null);
  const [analytics, setAnalytics] = useState<CampaignAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [resending, setResending] = useState<number | null>(null);

  const fetchCampaign = useCallback(async () => {
    if (!id) return;
    try {
      const response = await campaignService.getCampaignById(Number(id));
      setCampaign(response.data?.campaign || response.data);
    } catch (error: any) {
      console.error("Failed to fetch campaign:", error);
      toast.error(error.response?.data?.message || "Failed to fetch campaign");
    }
  }, [id]);

  const fetchAnalytics = useCallback(async () => {
    if (!id) return;
    try {
      setAnalyticsLoading(true);
      const response = await campaignService.getCampaignAnalytics(Number(id));
      setAnalytics(response.data);
    } catch (error: any) {
      console.error("Failed to fetch analytics:", error);
      // Don't show error toast for analytics, just log it
    } finally {
      setAnalyticsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const loadData = async () => {
      if (!id) return;
      setLoading(true);
      await Promise.all([fetchCampaign(), fetchAnalytics()]);
      setLoading(false);
    };
    loadData();

    // Auto-refresh analytics for active campaigns every 30 seconds
    const interval = setInterval(() => {
      if (analytics?.campaign.status === "SENDING" || analytics?.campaign.status === "ACTIVE") {
        void fetchAnalytics();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [id, fetchCampaign, fetchAnalytics, analytics?.campaign.status]);

  const handleSend = async () => {
    if (!id || !confirm("Are you sure you want to send this campaign now?")) return;
    try {
      setSending(true);
      await campaignService.sendCampaign(Number(id));
      toast.success("Campaign is being sent now");
      setTimeout(() => {
        void fetchCampaign();
        void fetchAnalytics();
      }, 1000);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to send campaign");
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !confirm("Are you sure you want to delete this campaign? This action cannot be undone.")) return;
    try {
      setDeleting(true);
      await campaignService.deleteCampaign(Number(id));
      toast.success("Campaign deleted successfully");
      navigate("/pages/campaigns");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to delete campaign");
    } finally {
      setDeleting(false);
    }
  };

  const handleRefresh = async () => {
    if (!id) return;
    try {
      setRefreshing(true);
      await Promise.all([fetchCampaign(), fetchAnalytics()]);
      toast.success("Campaign data refreshed");
    } catch (error: any) {
      toast.error("Failed to refresh campaign data");
    } finally {
      setRefreshing(false);
    }
  };

  const handleResend = async (deliveryRecordId: number) => {
    if (!confirm("Are you sure you want to resend this message?")) {
      return;
    }
    try {
      setResending(deliveryRecordId);
      await campaignService.resendDeliveryRecord(deliveryRecordId);
      toast.success("Message resent successfully");
      // Refresh analytics to get updated status
      await fetchAnalytics();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to resend message");
    } finally {
      setResending(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; label: string; bgColor: string; textColor: string }> = {
      DRAFT: { color: "gray", label: "Draft", bgColor: "bg-gray-100 dark:bg-gray-800", textColor: "text-gray-800 dark:text-gray-200" },
      SCHEDULED: { color: "info", label: "Scheduled", bgColor: "bg-blue-100 dark:bg-blue-900", textColor: "text-blue-800 dark:text-blue-200" },
      SENDING: { color: "warning", label: "Sending", bgColor: "bg-yellow-100 dark:bg-yellow-900", textColor: "text-yellow-800 dark:text-yellow-200" },
      ACTIVE: { color: "success", label: "Active", bgColor: "bg-green-100 dark:bg-green-900", textColor: "text-green-800 dark:text-green-200" },
      COMPLETED: { color: "success", label: "Completed", bgColor: "bg-emerald-100 dark:bg-emerald-900", textColor: "text-emerald-800 dark:text-emerald-200" },
    };
    const config = statusConfig[status] || { color: "gray", label: status, bgColor: "bg-gray-100", textColor: "text-gray-800" };
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${config.bgColor} ${config.textColor} border border-opacity-20`}>
        {config.label}
      </span>
    );
  };

  const getDeliveryStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; bgColor: string; textColor: string }> = {
      PENDING: { label: "Pending", bgColor: "bg-yellow-100 dark:bg-yellow-900", textColor: "text-yellow-800 dark:text-yellow-200" },
      SENT: { label: "Sent", bgColor: "bg-blue-100 dark:bg-blue-900", textColor: "text-blue-800 dark:text-blue-200" },
      DELIVERED: { label: "Delivered", bgColor: "bg-green-100 dark:bg-green-900", textColor: "text-green-800 dark:text-green-200" },
      BOUNCED: { label: "Bounced", bgColor: "bg-red-100 dark:bg-red-900", textColor: "text-red-800 dark:text-red-200" },
      FAILED: { label: "Failed", bgColor: "bg-red-100 dark:bg-red-900", textColor: "text-red-800 dark:text-red-200" },
      RINGING: { label: "Ringing", bgColor: "bg-blue-100 dark:bg-blue-900", textColor: "text-blue-800 dark:text-blue-200" },
      ANSWERED: { label: "Answered", bgColor: "bg-green-100 dark:bg-green-900", textColor: "text-green-800 dark:text-green-200" },
      NO_ANSWER: { label: "No Answer", bgColor: "bg-yellow-100 dark:bg-yellow-900", textColor: "text-yellow-800 dark:text-yellow-200" },
      BUSY: { label: "Busy", bgColor: "bg-yellow-100 dark:bg-yellow-900", textColor: "text-yellow-800 dark:text-yellow-200" },
      REJECTED: { label: "Rejected", bgColor: "bg-red-100 dark:bg-red-900", textColor: "text-red-800 dark:text-red-200" },
      DISCONNECTED: { label: "Disconnected", bgColor: "bg-red-100 dark:bg-red-900", textColor: "text-red-800 dark:text-red-200" },
    };
    const config = statusConfig[status] || { label: status, bgColor: "bg-gray-100", textColor: "text-gray-800" };
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${config.bgColor} ${config.textColor}`}>
        {config.label}
      </span>
    );
  };

  const getChannelIcon = (channel: string) => {
    const icons: Record<string, React.ReactNode> = {
      EMAIL: <FiMail className="text-base" />,
      SMS: <FiMessageSquare className="text-base" />,
      WHATSAPP: <FiMessageSquare className="text-base" />,
      TELEPHONY: <FiPhone className="text-base" />,
    };
    return icons[channel] || <HiMail className="text-base" />;
  };

  const formatDuration = (seconds: number | null | undefined) => {
    if (!seconds) return "-";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  if (loading) {
    return (
      <NavbarSidebarLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Spinner size="xl" />
        </div>
      </NavbarSidebarLayout>
    );
  }

  if (!campaign) {
    return (
      <NavbarSidebarLayout>
        <div className="px-4 pt-6 pb-8">
          <Alert color="failure">
            <span>Campaign not found</span>
          </Alert>
        </div>
      </NavbarSidebarLayout>
    );
  }

  const hasTelephony = analytics?.channelBreakdown.TELEPHONY && analytics.channelBreakdown.TELEPHONY > 0;
  const showCallMetrics = hasTelephony && (
    (analytics?.metrics.answered !== undefined && analytics.metrics.answered > 0) ||
    (analytics?.metrics.noAnswer !== undefined && analytics.metrics.noAnswer > 0) ||
    (analytics?.metrics.busy !== undefined && analytics.metrics.busy > 0) ||
    (analytics?.metrics.rejected !== undefined && analytics.metrics.rejected > 0) ||
    (analytics?.metrics.disconnected !== undefined && analytics.metrics.disconnected > 0)
  );

  return (
    <NavbarSidebarLayout>
      <div className="px-4 pt-6 pb-8">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-6">
          <Breadcrumb.Item onClick={() => navigate("/")}>
            <div className="flex items-center gap-x-2 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              <HiHome className="text-lg" />
              <span className="dark:text-white font-medium">Home</span>
            </div>
          </Breadcrumb.Item>
          <Breadcrumb.Item onClick={() => navigate("/pages/campaigns")}>
            <span className="cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors font-medium">Campaigns</span>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span className="text-gray-600 dark:text-gray-400 font-medium">{campaign.name}</span>
          </Breadcrumb.Item>
        </Breadcrumb>

        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
                <HiChartBar className="text-3xl text-white" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
                    {campaign.name}
                  </h1>
                  {getStatusBadge(campaign.status)}
                </div>
                {campaign.description && (
                  <p className="text-gray-600 dark:text-gray-400 text-base mt-1">
                    {campaign.description}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                color="gray"
                onClick={() => navigate("/pages/campaigns")}
                className="font-medium"
              >
                <HiChevronLeft className="mr-2" />
                Back
              </Button>
              <Button
                color="gray"
                onClick={handleRefresh}
                disabled={refreshing}
                className="font-medium"
              >
                <HiRefresh className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? "Refreshing..." : "Refresh"}
              </Button>
              {campaign.status === "DRAFT" && (
                <Button
                  color="success"
                  onClick={handleSend}
                  disabled={sending}
                  className="font-medium"
                >
                  <HiPaperAirplane className="mr-2" />
                  {sending ? "Sending..." : "Send Now"}
                </Button>
              )}
              <Button
                color="failure"
                onClick={handleDelete}
                disabled={deleting}
                className="font-medium"
              >
                <HiTrash className="mr-2" />
                {deleting ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </div>

        {/* Analytics Metrics Cards */}
        {analytics && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 border-l-4 border-l-blue-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Sent</p>
                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                      {analytics.metrics.total.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Total messages sent</p>
                  </div>
                  <div className="p-3 bg-blue-200 dark:bg-blue-700 rounded-lg">
                    <HiPaperAirplane className="text-2xl text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
              </Card>

              <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900 dark:to-green-800 border-l-4 border-l-green-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Delivered</p>
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                      {analytics.metrics.delivered.toLocaleString()}
                    </p>
                    <div className="mt-2">
                      <Badge color="success" className="text-xs">
                        {analytics.rates.deliveryRate.toFixed(1)}% delivery rate
                      </Badge>
                    </div>
                  </div>
                  <div className="p-3 bg-green-200 dark:bg-green-700 rounded-lg">
                    <HiCheckCircle className="text-2xl text-green-600 dark:text-green-400" />
                  </div>
                </div>
              </Card>

              <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900 dark:to-yellow-800 border-l-4 border-l-yellow-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Bounced</p>
                    <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">
                      {analytics.metrics.bounced.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {analytics.metrics.total > 0 
                        ? `${((analytics.metrics.bounced / analytics.metrics.total) * 100).toFixed(1)}% bounce rate`
                        : "No bounces"
                      }
                    </p>
                  </div>
                  <div className="p-3 bg-yellow-200 dark:bg-yellow-700 rounded-lg">
                    <HiExclamationCircle className="text-2xl text-yellow-600 dark:text-yellow-400" />
                  </div>
                </div>
              </Card>

              <Card className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900 dark:to-red-800 border-l-4 border-l-red-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Failed</p>
                    <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                      {analytics.metrics.failed.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {analytics.metrics.total > 0 
                        ? `${((analytics.metrics.failed / analytics.metrics.total) * 100).toFixed(1)}% failure rate`
                        : "No failures"
                      }
                    </p>
                  </div>
                  <div className="p-3 bg-red-200 dark:bg-red-700 rounded-lg">
                    <HiXCircle className="text-2xl text-red-600 dark:text-red-400" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Channel Breakdown - Only show channels that are actually used */}
            {(() => {
              const usedChannels = [];
              const channelConfig = [
                { 
                  key: 'EMAIL', 
                  name: 'Email', 
                  icon: <FiMail className="text-lg text-blue-600 dark:text-blue-400" />,
                  bgColor: 'bg-blue-50 dark:bg-blue-900/20',
                  borderColor: 'border-blue-200 dark:border-blue-700',
                  iconBg: 'bg-blue-100 dark:bg-blue-800',
                  textColor: 'text-blue-600 dark:text-blue-400',
                  count: analytics.channelBreakdown.EMAIL || 0
                },
                { 
                  key: 'SMS', 
                  name: 'SMS', 
                  icon: <FiMessageSquare className="text-lg text-green-600 dark:text-green-400" />,
                  bgColor: 'bg-green-50 dark:bg-green-900/20',
                  borderColor: 'border-green-200 dark:border-green-700',
                  iconBg: 'bg-green-100 dark:bg-green-800',
                  textColor: 'text-green-600 dark:text-green-400',
                  count: analytics.channelBreakdown.SMS || 0
                },
                { 
                  key: 'WHATSAPP', 
                  name: 'WhatsApp', 
                  icon: <FiMessageSquare className="text-lg text-emerald-600 dark:text-emerald-400" />,
                  bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
                  borderColor: 'border-emerald-200 dark:border-emerald-700',
                  iconBg: 'bg-emerald-100 dark:bg-emerald-800',
                  textColor: 'text-emerald-600 dark:text-emerald-400',
                  count: analytics.channelBreakdown.WHATSAPP || 0
                },
                { 
                  key: 'TELEPHONY', 
                  name: 'Telephony', 
                  icon: <FiPhone className="text-lg text-purple-600 dark:text-purple-400" />,
                  bgColor: 'bg-purple-50 dark:bg-purple-900/20',
                  borderColor: 'border-purple-200 dark:border-purple-700',
                  iconBg: 'bg-purple-100 dark:bg-purple-800',
                  textColor: 'text-purple-600 dark:text-purple-400',
                  count: analytics.channelBreakdown.TELEPHONY || 0
                }
              ];

              // Only include channels that have data
              channelConfig.forEach(channel => {
                if (channel.count > 0) {
                  usedChannels.push(channel);
                }
              });

              if (usedChannels.length === 0) return null;

              const gridCols = usedChannels.length === 1 ? 'lg:grid-cols-1' : 
                               usedChannels.length === 2 ? 'lg:grid-cols-2' : 
                               usedChannels.length === 3 ? 'lg:grid-cols-3' : 
                               'lg:grid-cols-4';

              return (
                <Card className="mb-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                      <HiChartBar className="text-xl text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">Channel Breakdown</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Messages sent by channel</p>
                    </div>
                  </div>
                  <div className={`grid grid-cols-1 md:grid-cols-2 ${gridCols} gap-4`}>
                    {usedChannels.map((channel) => (
                      <div key={channel.key} className={`p-4 ${channel.bgColor} rounded-lg border ${channel.borderColor}`}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`p-2 ${channel.iconBg} rounded-lg`}>
                            {channel.icon}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{channel.name}</p>
                            <p className={`text-2xl font-bold ${channel.textColor}`}>
                              {channel.count.toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {analytics.metrics.total > 0 
                            ? `${((channel.count / analytics.metrics.total) * 100).toFixed(1)}%`
                            : "0%"
                          }
                        </p>
                      </div>
                    ))}
                  </div>
                </Card>
              );
            })()}

            {/* Telephony Call Metrics */}
            {showCallMetrics && (
              <Card className="mb-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                    <FiPhone className="text-xl text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">Call Metrics</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Telephony campaign call statistics</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {analytics.metrics.answered !== undefined && analytics.metrics.answered > 0 && (
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
                      <div className="flex items-center gap-2 mb-2">
                        <HiCheckCircle className="text-lg text-green-600 dark:text-green-400" />
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Answered</p>
                      </div>
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {analytics.metrics.answered.toLocaleString()}
                      </p>
                      {analytics.metrics.total > 0 && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          {((analytics.metrics.answered / analytics.metrics.total) * 100).toFixed(1)}% answer rate
                        </p>
                      )}
                    </div>
                  )}

                  {analytics.metrics.noAnswer !== undefined && analytics.metrics.noAnswer > 0 && (
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700">
                      <div className="flex items-center gap-2 mb-2">
                        <FiPhoneOff className="text-lg text-yellow-600 dark:text-yellow-400" />
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">No Answer</p>
                      </div>
                      <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                        {analytics.metrics.noAnswer.toLocaleString()}
                      </p>
                    </div>
                  )}

                  {analytics.metrics.busy !== undefined && analytics.metrics.busy > 0 && (
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700">
                      <div className="flex items-center gap-2 mb-2">
                        <HiClock className="text-lg text-yellow-600 dark:text-yellow-400" />
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Busy</p>
                      </div>
                      <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                        {analytics.metrics.busy.toLocaleString()}
                      </p>
                    </div>
                  )}

                  {analytics.metrics.rejected !== undefined && analytics.metrics.rejected > 0 && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700">
                      <div className="flex items-center gap-2 mb-2">
                        <HiX className="text-lg text-red-600 dark:text-red-400" />
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Rejected</p>
                      </div>
                      <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                        {analytics.metrics.rejected.toLocaleString()}
                      </p>
                    </div>
                  )}

                  {analytics.metrics.disconnected !== undefined && analytics.metrics.disconnected > 0 && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700">
                      <div className="flex items-center gap-2 mb-2">
                        <HiLightningBolt className="text-lg text-red-600 dark:text-red-400" />
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Disconnected</p>
                      </div>
                      <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                        {analytics.metrics.disconnected.toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </Card>
            )}
          </>
        )}

        {/* Delivery Records Table */}
        {analytics && analytics.deliveryRecords && analytics.deliveryRecords.length > 0 && (
          <Card className="mb-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <HiMail className="text-xl text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Customer Delivery Status</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Individual customer delivery records</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table className="min-w-full">
                <Table.Head className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <Table.HeadCell className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Contact Name</Table.HeadCell>
                  <Table.HeadCell className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Email</Table.HeadCell>
                  <Table.HeadCell className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Channel</Table.HeadCell>
                  <Table.HeadCell className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</Table.HeadCell>
                  {hasTelephony && <Table.HeadCell className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Call Duration</Table.HeadCell>}
                  <Table.HeadCell className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Sent At</Table.HeadCell>
                  <Table.HeadCell className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Error</Table.HeadCell>
                  <Table.HeadCell className="px-6 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y divide-gray-200 dark:divide-gray-700">
                  {analytics.deliveryRecords.map((record, index) => (
                    <Table.Row 
                      key={record.id} 
                      className={`${index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900'} hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
                    >
                      <Table.Cell className="px-6 py-4 whitespace-nowrap align-middle">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {record.contact.firstName} {record.contact.lastName}
                        </div>
                      </Table.Cell>
                      <Table.Cell className="px-6 py-4 whitespace-nowrap align-middle">
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {record.contact.email || "-"}
                        </span>
                      </Table.Cell>
                      <Table.Cell className="px-6 py-4 whitespace-nowrap align-middle">
                        <div className="flex items-center gap-2">
                          {getChannelIcon(record.channel)}
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{record.channel}</span>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="px-6 py-4 whitespace-nowrap align-middle">
                        {getDeliveryStatusBadge(record.status)}
                      </Table.Cell>
                      {hasTelephony && (
                        <Table.Cell className="px-6 py-4 whitespace-nowrap align-middle">
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {record.channel === "TELEPHONY" && record.callDuration && record.callDuration > 0 ? (
                              <span className="font-medium text-green-600 dark:text-green-400">
                                {formatDuration(record.callDuration)}
                              </span>
                            ) : (
                              "-"
                            )}
                          </span>
                        </Table.Cell>
                      )}
                      <Table.Cell className="px-6 py-4 whitespace-nowrap align-middle">
                        <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <HiClock className="text-base text-gray-500" />
                          <span>
                            {(() => {
                              // Handle null, undefined, 0, "0", or epoch 0 dates
                              if (!record.sentAt || record.sentAt === "0" || record.sentAt === 0 || record.sentAt === "1970-01-01T00:00:00.000Z") {
                                return "-";
                              }
                              const date = new Date(record.sentAt);
                              // Check if date is valid and not epoch 0
                              if (!isNaN(date.getTime()) && date.getTime() > 0) {
                                return date.toLocaleString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                });
                              }
                              return "-";
                            })()}
                          </span>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="px-6 py-4 align-middle">
                        {record.errorMessage ? (
                          <div className="flex items-center gap-2 max-w-xs">
                            <FiAlertTriangle className="text-sm text-red-600 dark:text-red-400 flex-shrink-0" />
                            <span 
                              className="text-sm text-red-600 dark:text-red-400 truncate" 
                              title={record.errorMessage}
                            >
                              {record.errorMessage.length > 50 
                                ? `${record.errorMessage.substring(0, 50)}...`
                                : record.errorMessage
                              }
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </Table.Cell>
                      <Table.Cell className="px-6 py-4 align-middle">
                        <div className="flex items-center justify-center gap-2">
                          {(record.status === "FAILED" || record.status === "PENDING") && 
                           record.channel !== "WHATSAPP" && (
                            <Button
                              size="sm"
                              color="success"
                              onClick={() => handleResend(record.id)}
                              disabled={resending === record.id}
                              className="font-medium"
                              title="Resend"
                            >
                              {resending === record.id ? (
                                <>
                                  <HiRefresh className="mr-1 animate-spin" />
                                  Resending...
                                </>
                              ) : (
                                <>
                                  <HiPaperAirplane className="mr-1" />
                                  Resend
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          </Card>
        )}

        {/* Campaign Information (Only if no analytics or as fallback) */}
        {!analytics && (
          <Card className="mb-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <HiInformationCircle className="text-xl text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Campaign Information</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 block">
                  Campaign Name
                </Label>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{campaign.name}</p>
              </div>
              {campaign.description && (
                <div className="md:col-span-2">
                  <Label className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 block">
                    Description
                  </Label>
                  <p className="text-gray-700 dark:text-gray-300">{campaign.description}</p>
                </div>
              )}
              <div>
                <Label className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 block">
                  Status
                </Label>
                <div className="mt-1">{getStatusBadge(campaign.status)}</div>
              </div>
              <div>
                <Label className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 block">
                  Target Type
                </Label>
                <div className="flex items-center gap-2 text-gray-900 dark:text-white mt-1">
                  <HiUserGroup className="text-lg text-gray-500" />
                  <span className="font-medium">
                    {campaign.targetType?.replace(/_/g, " ") || "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Loading state for analytics */}
        {analyticsLoading && !analytics && (
          <Card className="mb-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-center py-12">
              <Spinner size="xl" />
              <span className="ml-3 text-gray-600 dark:text-gray-400">Loading analytics...</span>
            </div>
          </Card>
        )}

        {/* No delivery records message */}
        {analytics && (!analytics.deliveryRecords || analytics.deliveryRecords.length === 0) && (
          <Card className="mb-6 border border-gray-200 dark:border-gray-700">
            <div className="flex flex-col items-center justify-center py-12">
              <HiMail className="text-4xl text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-600 dark:text-gray-400 mb-2">No delivery records found</p>
              <p className="text-sm text-gray-500 dark:text-gray-500">Delivery records will appear here once the campaign is sent.</p>
            </div>
          </Card>
        )}
      </div>
    </NavbarSidebarLayout>
  );
};

export default CampaignDetailPage;
